import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { isUndefined, result } from "lodash";

import logo from "../../assets/img/msp.min.svg";

const AppHeader = ({
  rightSlot,
  mobileBottomSlot,
  title = "CENTRÁLNÍ REGISTR OZNÁMENÍ",
  menuItems = [],
  onNavigate,
  isAuthenticated = false,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const toggleMenu = () => {
    setMobileMenuOpen((prev) => {
      if (prev) {
        setExpandedGroups({});
      }
      return !prev;
    });
  };

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, []);

  const handleMobileNavigate = (item) => {
    if (onNavigate) {
      onNavigate(item);
    }
    setMobileMenuOpen(false);
    setExpandedGroups({});
  };

  const topLevelItems = menuItems
    .filter((i) => isUndefined(i.parent))
    .filter((i) => result(i, "conditionFn", true))
    .filter((i) => i.id !== "publicLogin");

  // Login items for mobile menu (children of publicLogin group)
  const loginItems = menuItems
    .filter((i) => i && i.parent === "publicLogin")
    .filter((i) => Boolean(i.path) || Boolean(i.link));

  return (
    <header className="appHeader">
      <div className="appHeader__top">
        <div className="appHeader__brand">
          <Link to="/" className="appHeader__brandLink">
            <img className="appHeader__logo" src={logo} alt="Logo" />
          </Link>
        </div>

        <div className="appHeader__centerTitle">
          <Link to="/" className="appHeader__centerTitleLink">{title}</Link>
        </div>

        <div className="appHeader__right">{rightSlot}</div>

        <button
          type="button"
          className="appHeader__menuBtn"
          onClick={toggleMenu}
          aria-label="Menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className="appHeader__menuBtnLabel">MENU</span>
          {mobileMenuOpen ? (
            <span className="appHeader__menuBtnIcon">✕</span>
          ) : (
            <span className="appHeader__menuBtnIcon">☰</span>
          )}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="appHeader__mobileMenu">
          <nav className="appHeader__mobileNav">
            {/* Login section for unauthenticated users (collapsible) */}
            {!isAuthenticated && loginItems.length > 0 && (
              <div className="appHeader__mobileNavGroup">
                <button
                  type="button"
                  className="appHeader__mobileNavGroupTitle"
                  onClick={() => toggleGroup("_login")}
                  aria-expanded={!!expandedGroups["_login"]}
                >
                  <span>PŘIHLÁSIT SE JAKO</span>
                  <span className="appHeader__mobileNavGroupArrow">
                    {expandedGroups["_login"] ? "▲" : "▼"}
                  </span>
                </button>
                {expandedGroups["_login"] && (
                  <div className="appHeader__mobileNavGroupChildren">
                    {loginItems.map((item) => {
                      if (!isUndefined(item.link)) {
                        return (
                          <a
                            key={item.id || item.link}
                            className="appHeader__mobileNavChild"
                            href={item.link}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span>{item.primaryText}</span>
                          </a>
                        );
                      }
                      return (
                        <button
                          key={item.id || item.path}
                          type="button"
                          className="appHeader__mobileNavChild"
                          onClick={() => handleMobileNavigate(item)}
                        >
                          <span>{item.primaryText}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {topLevelItems.map((item) => {
              const children = menuItems
                .filter((c) => c.parent === item.id)
                .filter((c) => result(c, "conditionFn", true));

              if (!isUndefined(item.link)) {
                return (
                  <a
                    key={item.id}
                    className="appHeader__mobileNavItem"
                    href={item.link}
                    target={item.target}
                    rel={
                      item.target === "_blank" ? "noopener noreferrer" : undefined
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{item.primaryText}</span>
                    {item.srHint && (
                      <span className="visually-hidden">{item.srHint}</span>
                    )}
                  </a>
                );
              }

              if (children.length === 0) {
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="appHeader__mobileNavItem"
                    onClick={() => handleMobileNavigate(item)}
                  >
                    <span>{item.primaryText}</span>
                  </button>
                );
              }

              const isExpanded = !!expandedGroups[item.id];

              return (
                <div key={item.id} className="appHeader__mobileNavGroup">
                  <button
                    type="button"
                    className="appHeader__mobileNavGroupTitle"
                    onClick={() => toggleGroup(item.id)}
                    aria-expanded={isExpanded}
                  >
                    <span>{item.primaryText}</span>
                    <span className="appHeader__mobileNavGroupArrow">
                      {isExpanded ? "▲" : "▼"}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="appHeader__mobileNavGroupChildren">
                      {children.map((child) => (
                        <button
                          key={child.id}
                          type="button"
                          className="appHeader__mobileNavChild"
                          onClick={() => handleMobileNavigate(child)}
                        >
                          <span>{child.primaryText}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          {mobileBottomSlot && (
            <div className="appHeader__mobileBottom">{mobileBottomSlot}</div>
          )}
        </div>
      )}
    </header>
  );
};

AppHeader.propTypes = {
  rightSlot: PropTypes.node,
  mobileBottomSlot: PropTypes.node,
  title: PropTypes.string,
  menuItems: PropTypes.array,
  onNavigate: PropTypes.func,
  isAuthenticated: PropTypes.bool,
};

export default AppHeader;
